'use client'

import { useMemo, useState, type FormEvent } from 'react'
import { useRouter } from 'next/navigation'
import {
  FieldGroup,
  FieldLabel,
  PrimaryButton,
  SelectField,
  TextareaField,
  TextField,
} from '@/app/FormFields'
import type { KnowledgeEntryKind } from '@/db/schema'

type KnowledgeFormProps = {
  entry?: {
    id: string
    title: string
    slug: string
    kind: KnowledgeEntryKind
    bodyMarkdown: string
  }
}

const KINDS: KnowledgeEntryKind[] = ['index', 'log', 'entity', 'concept', 'procedure']

export default function KnowledgeForm({ entry }: KnowledgeFormProps) {
  const router = useRouter()
  const [title, setTitle] = useState(entry?.title ?? '')
  const [slug, setSlug] = useState(entry?.slug ?? '')
  const [kind, setKind] = useState<KnowledgeEntryKind>(entry?.kind ?? 'concept')
  const [bodyMarkdown, setBodyMarkdown] = useState(entry?.bodyMarkdown ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const preview = useMemo(() => {
    const trimmed = bodyMarkdown.trim()
    return trimmed.length > 260 ? `${trimmed.slice(0, 257)}...` : trimmed
  }, [bodyMarkdown])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSaving(true)

    const response = await fetch(entry ? `/api/knowledge/${entry.id}` : '/api/knowledge', {
      method: entry ? 'PUT' : 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ title, slug, kind, bodyMarkdown }),
    })

    const payload = await response.json().catch(() => null) as
      | { entry?: { id: string }; error?: string }
      | null

    setSaving(false)
    if (!response.ok || !payload?.entry?.id) {
      setError(payload?.error ?? 'Unable to save knowledge entry')
      return
    }

    router.push(`/knowledge/${payload.entry.id}`)
    router.refresh()
  }

  return (
    <form onSubmit={submit} className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <FieldGroup>
          <FieldLabel htmlFor="kb-title">Title</FieldLabel>
          <TextField
            id="kb-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Line 3 filler E42 fault"
            required
          />
        </FieldGroup>

        <div className="grid gap-4 md:grid-cols-[1fr_180px]">
          <FieldGroup>
            <FieldLabel htmlFor="kb-slug">Slug</FieldLabel>
            <TextField
              id="kb-slug"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="auto-generated if blank"
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel htmlFor="kb-kind">Type</FieldLabel>
            <SelectField
              id="kb-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as KnowledgeEntryKind)}
            >
              {KINDS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </SelectField>
          </FieldGroup>
        </div>

        <FieldGroup>
          <FieldLabel htmlFor="kb-markdown">Markdown</FieldLabel>
          <TextareaField
            id="kb-markdown"
            value={bodyMarkdown}
            onChange={(event) => setBodyMarkdown(event.target.value)}
            className="min-h-[420px] font-mono text-sm"
            placeholder="## Symptoms&#10;- Intermittent shutdowns..."
            required
          />
        </FieldGroup>

        {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}

        <PrimaryButton
          type="submit"
          disabled={saving}
        >
          {saving ? 'Saving...' : entry ? 'Update entry' : 'Create entry'}
        </PrimaryButton>
      </div>

      <aside className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">Preview</h2>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-700 dark:text-zinc-300">
          {preview || 'Markdown preview appears here while you write.'}
        </p>
      </aside>
    </form>
  )
}
